/**
 * Idempotency-Key helpers for mutating POSTs.
 *
 * CLAUDE-A's `apiRequest` already mints a UUIDv7 per POST call and reuses it
 * across its internal network/5xx retries. What it cannot know is the span of
 * a LOGICAL action: when a submit fails and the user clicks "Save" again,
 * that is the same action and must replay the SAME key so the server
 * deduplicates it. `useIdempotentMutation` owns that span — one key per
 * logical action, held across ambiguous network/5xx retries and a canonical
 * key-reuse hard stop, and rotated when the payload changes or another
 * definitive failure makes a corrected submit new.
 */
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useRef } from "react";
import { v7 as uuidv7 } from "uuid";

import { ApiError, NetworkError } from "@/lib/api/errors";

/** Stable machine code the server returns for same-key/different-payload. */
export const IDEMPOTENCY_KEY_REUSE_CODE = "platform.idempotency.key_reuse";

/** Fresh UUIDv7 Idempotency-Key (time-ordered, per SPEC-CONVENTIONS). */
export function newIdempotencyKey(): string {
  return uuidv7();
}

/**
 * True for the canonical 409 `platform.idempotency.key_reuse` problem — the
 * same Idempotency-Key was replayed with a DIFFERENT payload. Screens surface this per their SPEC
 * (usually "this action was already submitted differently — reload").
 */
export function isIdempotencyKeyReuse(error: unknown): error is ApiError {
  return (
    error instanceof ApiError && error.status === 409 && error.code === IDEMPOTENCY_KEY_REUSE_CODE
  );
}

export interface IdempotencyAttempt {
  key: string;
  fingerprint: string;
}

/** Uploads are client-limited to 5 MiB; never buffer an unbounded Blob. */
export const MAX_IDEMPOTENCY_BLOB_BYTES = 5 * 1024 * 1024;

async function readBlobBounded(blob: Blob): Promise<ArrayBuffer> {
  if (blob.size > MAX_IDEMPOTENCY_BLOB_BYTES) {
    throw new RangeError(
      `Idempotency fingerprint input exceeds ${MAX_IDEMPOTENCY_BLOB_BYTES} bytes`,
    );
  }
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new TypeError("Blob fingerprint reader returned a non-binary result"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Blob fingerprint read failed"));
    reader.readAsArrayBuffer(blob);
  });
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) {
    throw new Error("Web Crypto SHA-256 is required for idempotent file uploads");
  }
  const digest = new Uint8Array(await subtle.digest("SHA-256", bytes));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function fingerprintValue(value: unknown, seen: WeakSet<object>): Promise<string> {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return `string:${JSON.stringify(value)}`;
  if (typeof value === "number") return `number:${String(value)}`;
  if (typeof value === "boolean") return `boolean:${String(value)}`;
  if (typeof value === "bigint") return `bigint:${String(value)}`;
  if (typeof value !== "object") return `${typeof value}:${String(value)}`;
  if (typeof File !== "undefined" && value instanceof File) {
    const digest = await sha256Hex(await readBlobBounded(value));
    return `file:${JSON.stringify([value.name, value.size, value.type, value.lastModified, digest])}`;
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    const digest = await sha256Hex(await readBlobBounded(value));
    return `blob:${JSON.stringify([value.size, value.type, digest])}`;
  }
  if (value instanceof Date) return `date:${value.toISOString()}`;
  if (seen.has(value)) throw new TypeError("Idempotent mutation variables must not be cyclic");
  seen.add(value);
  let result: string;
  if (Array.isArray(value)) {
    const parts: string[] = [];
    for (const item of value) parts.push(await fingerprintValue(item, seen));
    result = `array:[${parts.join(",")}]`;
  } else {
    const parts: string[] = [];
    for (const key of Object.keys(value).sort()) {
      parts.push(
        `${JSON.stringify(key)}:${await fingerprintValue((value as Record<string, unknown>)[key], seen)}`,
      );
    }
    result = `object:{${parts.join(",")}}`;
  }
  seen.delete(value);
  return result;
}

/** Deterministic client-side identity for one logical mutation payload. */
export function idempotencyFingerprint(payload: unknown): Promise<string> {
  return fingerprintValue(payload, new WeakSet());
}

/** Reuse only when the next attempt carries the exact same logical payload. */
export async function idempotencyAttemptFor(
  previous: IdempotencyAttempt | null,
  payload: unknown,
): Promise<IdempotencyAttempt> {
  const fingerprint = await idempotencyFingerprint(payload);
  return previous?.fingerprint === fingerprint
    ? previous
    : { key: newIdempotencyKey(), fingerprint };
}

/**
 * Atomically claim an attempt after asynchronous file hashing. Reading the
 * holder only after the digest resolves makes concurrent same-payload submits
 * converge on one key instead of each capturing the earlier null value.
 */
export async function claimIdempotencyAttempt(
  holder: { current: IdempotencyAttempt | null },
  payload: unknown,
): Promise<IdempotencyAttempt> {
  const fingerprint = await idempotencyFingerprint(payload);
  const attempt =
    holder.current?.fingerprint === fingerprint
      ? holder.current
      : { key: newIdempotencyKey(), fingerprint };
  holder.current = attempt;
  return attempt;
}

/** Network loss and exhausted 5xx responses leave the server outcome ambiguous. */
export function isAmbiguousIdempotencyFailure(error: unknown): boolean {
  return error instanceof NetworkError || (error instanceof ApiError && error.status >= 500);
}

export interface UseIdempotentMutationOptions<TData, TVariables, TContext = unknown> extends Omit<
  UseMutationOptions<TData, Error, TVariables, TContext>,
  "mutationFn"
> {
  /**
   * Perform the request, passing `idempotencyKey` through to the api layer:
   * `(vars, key) => createOrder(vars, { idempotencyKey: key })` where the
   * feature api.ts forwards it as `apiFetch(path, { method: "POST", body,
   * idempotencyKey })`.
   */
  mutationFn: (variables: TVariables, idempotencyKey: string) => Promise<TData>;
}

/**
 * `useMutation` wrapper that scopes one Idempotency-Key to one logical action:
 *
 * - the key is minted lazily on the first `mutate` call;
 * - ambiguous network/5xx retries with the same payload replay the SAME key;
 * - changed variables or a definitive client/4xx failure rotate the key;
 * - canonical key reuse retains the key, preventing a blind new submission
 *   while the screen tells the user to reload;
 * - on success the key resets, so the next action mints a fresh one.
 *
 * Pair with `disabled={mutation.isPending}` on the trigger button — the key
 * makes replays safe, the disable makes double-clicks a non-event.
 */
export function useIdempotentMutation<TData = unknown, TVariables = void, TContext = unknown>(
  options: UseIdempotentMutationOptions<TData, TVariables, TContext>,
): UseMutationResult<TData, Error, TVariables, TContext> {
  const { mutationFn, onSuccess, onError, ...rest } = options;
  const attemptRef = useRef<IdempotencyAttempt | null>(null);

  return useMutation<TData, Error, TVariables, TContext>({
    ...rest,
    mutationFn: async (variables) => {
      const attempt = await claimIdempotencyAttempt(attemptRef, variables);
      return mutationFn(variables, attempt.key);
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      if (attemptRef.current?.fingerprint === (await idempotencyFingerprint(variables))) {
        attemptRef.current = null;
      }
      return onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: async (error, variables, onMutateResult, context) => {
      let fingerprint: string | null = null;
      try {
        fingerprint = await idempotencyFingerprint(variables);
      } catch {
        // Fingerprinting itself failed before a request could be made.
      }
      if (
        !isAmbiguousIdempotencyFailure(error) &&
        !isIdempotencyKeyReuse(error) &&
        (fingerprint === null || attemptRef.current?.fingerprint === fingerprint)
      ) {
        // A normal definitive 4xx/client failure did not leave an uncertain
        // commit; a corrected resubmission is a new action. Key reuse is the
        // exception: the original result exists, so only reload/payload change
        // may escape that hard stop.
        attemptRef.current = null;
      }
      return onError?.(error, variables, onMutateResult, context);
    },
  });
}
